import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Text,
  Title,
  Group,
  SimpleGrid,
  ThemeIcon,
  Table,
  Badge,
  Loader,
  Center,
  ScrollArea,
  Card,
  Alert,
  Button,
  RingProgress,
  ActionIcon,
  Tooltip,
  Avatar
} from "@mantine/core";
import {
  IconStethoscope,     // Ikon Nutrisi
  IconClipboardCheck,  // Ikon Monitoring
  IconMapPin,
  IconRefresh,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconActivity
} from "@tabler/icons-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

export default function SupervisorDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/supervisor/dashboard");
      setData(res.data);
    } catch (err) {
      console.error("Gagal memuat dashboard:", err);
      setError("Gagal mengambil data dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) return <Center h="100vh"><Loader size="xl" /></Center>;

  if (error || !data) {
    return (
      <Center h="80vh" style={{ flexDirection: 'column' }}>
         <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mb="md">
            {error || "Data tidak ditemukan."}
         </Alert>
         <Button variant="subtle" onClick={fetchDashboard} leftSection={<IconRefresh size={16}/>}>
            Coba Refresh
         </Button>
      </Center>
    );
  }

  const { 
    supervisor_nama = "Supervisor",
    stats = { hari_ini: 0, pending: 0, kinerja_bulan_ini: 0 }, 
    tugas_prioritas = [], 
    chart = [] 
  } = data;

  // --- ROWS TUGAS PRIORITAS ---
  const rows = tugas_prioritas.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>
        <Text fz="sm" fw={500}>
            {new Date(item.tanggal).toLocaleDateString('id-ID', {day: 'numeric', month: 'short'})}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text fz="sm" fw={600}>{item.sekolah}</Text>
        <Text fz="xs" c="dimmed">{item.menu}</Text>
      </Table.Td>
      
      {/* Kolom Status Laporan Nutrisi */}
      <Table.Td>
         {item.has_nutrisi ? (
            <Badge color="green" variant="light" leftSection={<IconCheck size={10}/>}>Selesai</Badge>
         ) : (
            <Button 
                size="xs" 
                variant="outline" 
                color="red" 
                leftSection={<IconStethoscope size={14}/>}
                onClick={() => navigate('/supervisor/laporan-nutrisi')}
            >
                Input Gizi
            </Button>
         )}
      </Table.Td>

      {/* Kolom Status Laporan Monitoring */}
      <Table.Td>
         {item.has_monitoring ? (
            <Badge color="green" variant="light" leftSection={<IconCheck size={10}/>}>Selesai</Badge>
         ) : (
            <Button 
                size="xs" 
                variant="outline" 
                color="orange" 
                leftSection={<IconClipboardCheck size={14}/>}
                onClick={() => navigate('/supervisor/laporan-monitoring')}
            >
                Input Monitor
            </Button>
         )}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div style={{ padding: 20 }}>
      <Group justify="space-between" mb="lg">
         <div>
            <Title order={2}>Halo, {supervisor_nama}</Title>
            <Text c="dimmed">Selamat bertugas! Pastikan kualitas makanan terjaga.</Text>
         </div>
         <Button variant="white" onClick={fetchDashboard} leftSection={<IconRefresh size={16}/>}>Refresh</Button>
      </Group>

      {/* 1. STATISTIK UTAMA */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        
        {/* Card Jadwal Hari Ini */}
        <Paper withBorder p="md" radius="md">
            <Group justify="space-between">
                <div>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Lokasi Hari Ini</Text>
                    <Text fw={700} size="xl">{stats.hari_ini} <span style={{fontSize: 14, fontWeight: 400}}>Sekolah</span></Text>
                    <Text size="xs" c="dimmed">Jadwal distribusi aktif</Text>
                </div>
                <ThemeIcon variant="light" color="blue" size="xl" radius="md">
                    <IconMapPin size={24}/>
                </ThemeIcon>
            </Group>
        </Paper>

        {/* Card Tugas Pending (Paling Penting) */}
        <Paper withBorder p="md" radius="md" bg="red.0">
            <Group justify="space-between">
                <div>
                    <Text size="xs" c="red.9" fw={700} tt="uppercase">Tugas Pending</Text>
                    <Text fw={700} size="xl" c="red.9">{stats.pending} <span style={{fontSize: 14, fontWeight: 400}}>Laporan</span></Text>
                    <Text size="xs" c="red.7">Belum diinput (Nutrisi/Monitor)</Text>
                </div>
                <ThemeIcon variant="filled" color="red" size="xl" radius="md">
                    <IconAlertCircle size={24}/>
                </ThemeIcon>
            </Group>
        </Paper>

        {/* Card Kinerja Saya */}
        <Paper withBorder p="md" radius="md">
            <Group justify="space-between">
                <div>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Laporan Saya (Bulan Ini)</Text>
                    <Text fw={700} size="xl">{stats.kinerja_bulan_ini}</Text>
                    <Text size="xs" c="dimmed">Total input laporan</Text>
                </div>
                <RingProgress
                    size={70}
                    roundCaps
                    thickness={6}
                    sections={[{ value: 100, color: 'teal' }]}
                    label={<Center><IconActivity size={20} color="teal" /></Center>}
                />
            </Group>
        </Paper>
      </SimpleGrid>

      <Grid>
        {/* 2. TABEL TUGAS PRIORITAS (Action Center) */}
        <Grid.Col span={{ base: 12, md: 8 }}>
            <Card withBorder shadow="sm" radius="md" h="100%">
                <Group justify="space-between" mb="md">
                    <Title order={4}>Tugas Prioritas</Title>
                    <Badge color="red" variant="dot">Segera Lengkapi</Badge>
                </Group>

                <ScrollArea>
                    <Table verticalSpacing="sm" highlightOnHover>
                        <Table.Thead bg="gray.0">
                            <Table.Tr>
                                <Table.Th>Tanggal</Table.Th>
                                <Table.Th>Lokasi</Table.Th>
                                <Table.Th>Status Gizi</Table.Th>
                                <Table.Th>Status Monitor</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.length > 0 ? rows : (
                                <Table.Tr><Table.Td colSpan={4} align="center" c="dimmed" py="xl">
                                    Semua pekerjaan beres! Tidak ada pending.
                                </Table.Td></Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            </Card>
        </Grid.Col>

        {/* 3. CHART TREN NUTRISI (Hasil Inputan Supervisor) */}
        <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder shadow="sm" radius="md">
                <Title order={4} mb="md">Tren Hasil Cek Gizi</Title>
                <div style={{ height: 280, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chart}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis 
                                dataKey="date" 
                                tickFormatter={(val) => new Date(val).toLocaleDateString('id-ID', {day:'numeric', month:'short'})}
                                tick={{fontSize: 10}}
                            />
                            <YAxis width={30} tick={{fontSize: 10}} />
                            <RechartsTooltip labelFormatter={(l) => new Date(l).toLocaleDateString('id-ID')}/>
                            <Legend />
                            <Line type="monotone" dataKey="avg_protein" name="Protein (g)" stroke="#228be6" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="avg_lemak" name="Lemak (g)" stroke="#fab005" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <Text size="xs" c="dimmed" ta="center" mt="sm">
                    Rata-rata hasil pemeriksaan nutrisi yang Anda input 7 hari terakhir.
                </Text>
            </Card>
        </Grid.Col>
      </Grid>
    </div>
  );
}